import CustomButton from "Components/Custom/CustomButton";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Badge } from "reactstrap";
import { TourKey } from "tour/tourKeys";

// export interface Step {
//   element: string;
//   title: string;
//   description: string;
//   action?: () => void;
//   cleanupAction?: () => void;
//   waitForElement?: boolean;
// }

/**
 * Overlay Tour Step Definition
 * -----------------------------------------
 * element           -> CSS selector or testID to highlight
 * title             -> Step heading
 * subtitle          -> Small explanation text
 * behavior          -> Controls buttons / navigation logic
 * action            -> Runs when step becomes active
 * cleanupAction     -> Runs when user leaves this step
 * waitForElement    -> If true, overlay waits until element appears
 */
export interface Step {
  element: string; // element can be optional (some steps are info-only)
  title: string;
  subtitle: string;

  behavior:
    | "next" // only Next / Finish
    | "previous_next" // Previous + Next
    | "ok" // only OK
    | "previous_ok"; // Previous + Ok

  action?: () => void;
  cleanupAction?: () => void;
  waitForElement?: boolean;
}

interface OverlayGuideProps {
  steps: Step[];
  open: boolean;
  currentStep: number;
  showBtns?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  onClose: () => void;
  key: TourKey;
}

const OverlayGuide: React.FC<OverlayGuideProps> = ({
  steps,
  open,
  onClose,
  onNext,
  onPrev,
  showBtns = true,
  currentStep,
  key,
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const behavior = steps[currentStep]?.behavior;

  useEffect(() => {
    if (open) {
      // Prevent user scrolling by setting overflow hidden
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${
        window.innerWidth - document.documentElement.clientWidth
      }px`; // Prevent layout shift

      // Function to block user scroll attempts
      const preventScroll = (e: WheelEvent | TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
      };

      // Add listeners to prevent user scroll
      document.addEventListener("wheel", preventScroll, { passive: false });
      document.addEventListener("touchmove", preventScroll, { passive: false });

      return () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.removeEventListener("wheel", preventScroll);
        document.removeEventListener("touchmove", preventScroll);
      };
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      updatePosition(currentStep);
    }
  }, [open, currentStep]);

  const onCloseorDone = () => {
    onClose();
  };

  const updatePosition = (index: number): void => {
    const step = steps[index];
    console.log("stepupdatePosition", step);
    if (!step) return;

    const tryFindElement = (retries = 0): void => {
      console.log("tryFindElement", step.element);
      const element = document.querySelector("#" + step.element);
      if (element) {
        const rect = element.getBoundingClientRect();
        // const isInViewport =
        //   rect.top >= 0 &&
        //   rect.left >= 0 &&
        //   rect.bottom <= window.innerHeight &&
        //   rect.right <= window.innerWidth;

        // if (!isInViewport) {
        //   // Scroll the element into view
        //   element.scrollIntoView({
        //     behavior: "smooth",
        //     block: "center",
        //     inline: "center",
        //   });

        //   // Wait longer for scroll to complete before updating position
        //   setTimeout(() => {
        //     const updatedRect = element.getBoundingClientRect();
        //     setTargetRect(updatedRect);
        //   }, 600); // Increased from 300ms to 600ms
        // } else {
        setTargetRect(rect);
        // }
      } else if (step.waitForElement && retries < 20) {
        setTimeout(() => tryFindElement(retries + 1), 100);
      } else if (retries < 20) {
        // Add retry even if waitForElement is not set
        setTimeout(() => tryFindElement(retries + 1), 100);
      }
    };

    tryFindElement();
  };

  if (!open || !targetRect) return null;

  const step = steps[currentStep];

  const isMobile = window.innerWidth < 640;
  const edgeMargin = isMobile ? 8 : 16;
  const padding = isMobile ? 12 : 20;

  const spaceBelow = window.innerHeight - targetRect.bottom;
  const spaceAbove = targetRect.top;
  const spaceRight = window.innerWidth - targetRect.right;
  const spaceLeft = targetRect.left;

  const estimatedContentHeight = Math.min(
    200 + (step?.subtitle?.length || 0) / 2.5,
    500,
  );

  interface PositionScore {
    position: "below" | "above" | "right" | "left";
    visibilityScore: number;
    widthPercent: number;
    heightPercent: number;
  }

  const calculatePositionScores = (): PositionScore[] => {
    const scores: PositionScore[] = [];

    const belowHeight = spaceBelow - padding - edgeMargin;
    const belowHeightPercent = (belowHeight / estimatedContentHeight) * 100;
    scores.push({
      position: "below",
      visibilityScore: Math.min(belowHeightPercent * 0.9, 90),
      widthPercent: 100,
      heightPercent: Math.min(belowHeightPercent, 90),
    });

    const aboveHeight = spaceAbove - padding - edgeMargin;
    const aboveHeightPercent = (aboveHeight / estimatedContentHeight) * 100;
    scores.push({
      position: "above",
      visibilityScore: Math.min(aboveHeightPercent * 0.6, 60),
      widthPercent: 100,
      heightPercent: Math.min(aboveHeightPercent, 60),
    });

    const rightWidth = spaceRight - padding - edgeMargin;
    const minTooltipWidth = isMobile ? 280 : 320;
    const rightWidthPercent = (rightWidth / minTooltipWidth) * 100;
    scores.push({
      position: "right",
      visibilityScore: rightWidthPercent >= 100 ? 100 : rightWidthPercent * 0.7,
      widthPercent: Math.min(rightWidthPercent, 100),
      heightPercent: 100,
    });

    const leftWidth = spaceLeft - padding - edgeMargin;
    const leftWidthPercent = (leftWidth / minTooltipWidth) * 100;
    scores.push({
      position: "left",
      visibilityScore: leftWidthPercent >= 100 ? 95 : leftWidthPercent * 0.8,
      widthPercent: Math.min(leftWidthPercent, 100),
      heightPercent: 100,
    });

    return scores;
  };

  const scores = calculatePositionScores();
  const bestPosition = scores.reduce((best, current) =>
    current.visibilityScore > best.visibilityScore ? current : best,
  );

  const tooltipPosition = bestPosition.position;

  const getTooltipStyle = (): React.CSSProperties => {
    let top: number | undefined;
    let bottom: number | undefined;
    let left: number | undefined;
    let right: number | undefined;
    let maxHeight: string | undefined;
    let width: string | number | undefined;
    let maxWidth: string | undefined;

    if (tooltipPosition === "below") {
      top = targetRect.bottom + padding;
      left = isMobile ? edgeMargin : Math.max(edgeMargin, targetRect.left);
      maxHeight = `${Math.max(150, spaceBelow - padding - edgeMargin)}px`;
      width = isMobile
        ? `${window.innerWidth - edgeMargin * 2}px`
        : `${Math.min(400, window.innerWidth - edgeMargin * 2)}px`;
      maxWidth = `${window.innerWidth - edgeMargin * 2}px`;
    } else if (tooltipPosition === "above") {
      bottom = window.innerHeight - targetRect.top + padding;
      left = isMobile ? edgeMargin : Math.max(edgeMargin, targetRect.left);
      maxHeight = `${Math.max(
        150,
        Math.min(spaceAbove * 0.6, spaceAbove - padding - edgeMargin),
      )}px`;
      width = isMobile
        ? `${window.innerWidth - edgeMargin * 2}px`
        : `${Math.min(400, window.innerWidth - edgeMargin * 2)}px`;
      maxWidth = `${window.innerWidth - edgeMargin * 2}px`;
    } else if (tooltipPosition === "right") {
      top = Math.max(
        edgeMargin,
        Math.min(targetRect.top, window.innerHeight - 300 - edgeMargin),
      );
      left = targetRect.right + padding;
      maxHeight = `${window.innerHeight - edgeMargin * 2}px`;
      width = `${Math.min(400, spaceRight - padding - edgeMargin)}px`;
      maxWidth = `${spaceRight - padding - edgeMargin}px`;
    } else if (tooltipPosition === "left") {
      top = Math.max(
        edgeMargin,
        Math.min(targetRect.top, window.innerHeight - 300 - edgeMargin),
      );
      right = window.innerWidth - targetRect.left + padding;
      maxHeight = `${window.innerHeight - edgeMargin * 2}px`;
      width = `${Math.min(400, spaceLeft - padding - edgeMargin)}px`;
      maxWidth = `${spaceLeft - padding - edgeMargin}px`;
    }

    return {
      top,
      bottom,
      left,
      right,
      maxHeight,
      width,
      maxWidth,
      overflowY: tooltipPosition === "below" ? "auto" : ("hidden" as const),
      overflowX: "hidden" as const,
    };
  };

  const getArrowStyle = (): React.CSSProperties => {
    const tooltipStyle = getTooltipStyle();
    const tooltipWidth =
      typeof tooltipStyle.width === "string"
        ? parseInt(tooltipStyle.width)
        : tooltipStyle.width || 320;

    if (tooltipPosition === "below") {
      const tooltipLeft = tooltipStyle.left || 0;
      const arrowLeft =
        targetRect.left + targetRect.width / 2 - Number(tooltipLeft);
      return {
        top: -10,
        left: Math.max(15, Math.min(arrowLeft, tooltipWidth - 35)),
      };
    } else if (tooltipPosition === "above") {
      const tooltipLeft = tooltipStyle.left || 0;
      const arrowLeft =
        targetRect.left + targetRect.width / 2 - Number(tooltipLeft);
      return {
        bottom: -10,
        left: Math.max(15, Math.min(arrowLeft, tooltipWidth - 35)),
      };
    } else if (tooltipPosition === "right") {
      return {
        left: -10,
        top: Math.max(20, targetRect.height / 2),
      };
    } else if (tooltipPosition === "left") {
      return {
        right: -10,
        top: Math.max(20, targetRect.height / 2),
      };
    }
    return {};
  };

  const getArrowRotation = (): string => {
    if (tooltipPosition === "below") return "rotate(45deg)";
    if (tooltipPosition === "above") return "rotate(225deg)";
    if (tooltipPosition === "right") return "rotate(315deg)";
    if (tooltipPosition === "left") return "rotate(135deg)";
    return "rotate(45deg)";
  };

  const overlayContent = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        transition: "all 0.3s",
      }}
    >
      {/* Backdrop */}
      <div
        style={{
          position: "absolute",
          inset: 0,
        }}
      />

      {/* Spotlight cutout */}
      <svg
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - 8}
              y={targetRect.top - 8}
              width={targetRect.width + 16}
              height={targetRect.height + 16}
              rx="12"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.5)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Animated border */}
      <div
        style={{
          position: "absolute",
          border: "3px solid #082B60",
          borderRadius: "12px",
          transition: "all 0.5s ease-out",
          pointerEvents: "none",
          top: targetRect.top - 8,
          left: targetRect.left - 8,
          width: targetRect.width + 16,
          height: targetRect.height + 16,
          animation: "pulseGlow 2s ease-in-out infinite",
        }}
      />

      {/* Tooltip Card */}
      <div
        className="bg-white shadow-lg"
        style={{
          position: "absolute",
          ...getTooltipStyle(),
          transition: "all 0.5s ease-out",
          animation: "slideIn 0.4s ease-out",
          borderRadius: "12px",
        }}
      >
        {/* Arrow */}
        <div
          style={{
            position: "absolute",
            width: "20px",
            height: "20px",
            // backgroundColor: "red",
            transform: getArrowRotation(),
            ...getArrowStyle(),
            boxShadow: "-2px -2px 4px rgba(0, 0, 0, 0.1)",
          }}
          className="custom-scroll"
        />

        {/* Content */}
        <div className="p-4 br-12" style={{ position: "relative" }}>
          {/* Step indicator */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <Badge
                color="primary"
                pill
                className="d-flex align-items-center justify-content-center fw-bold"
                style={{ width: "32px", height: "32px", fontSize: "0.875rem" }}
              >
                {currentStep + 1}
              </Badge>
              <small className="text-muted fw-medium">
                Step {currentStep + 1} of {steps.length}
              </small>
            </div>
            <Button
              close
              onClick={onCloseorDone}
              className="btn-sm"
              aria-label="Close"
            />
          </div>
          {/* Title */}
          <h5 className="fw-bold mb-3">{step?.title || ""}</h5>
          {/* Description */}
          <p
            className="text-muted mb-4"
            style={{ fontSize: "0.875rem", lineHeight: "1.6" }}
          >
            {step?.subtitle || ""}
          </p>
          {/* Progress dots */}
          {steps.length < 6 && (
            <div className="d-flex gap-1 mb-4">
              {steps.map((_, index) => (
                <div
                  key={index}
                  style={{
                    height: "6px",
                    borderRadius: "3px",
                    transition: "all 0.3s",
                    backgroundColor:
                      index === currentStep
                        ? "#082B60"
                        : index < currentStep
                        ? "#355A8A"
                        : "#89909D",
                    width: index === currentStep ? "32px" : "6px",
                  }}
                />
              ))}
            </div>
          )}
          {/* Navigation buttons */}
          {/* {showBtns && (
            <div className="d-flex gap-2">
              {current > 0 && (
                <CustomButton
                  text={"Previous"}
                  className=" br-7 flex-fill bg-F2F4FB text-black2D"
                  textColor="text-black2D"
                  textClassName="fw-medium"
                  onClick={handlePrevious}
                />
              )}
              <CustomButton
                text={current === steps.length - 1 ? "Finish" : "Next"}
                className="bg-primary br-7 flex-fill"
                textClassName="text-white fw-medium"
                onClick={handleNext}
              />
            </div>
          )} */}

          {showBtns && (
            <div className="d-flex gap-2">
              {/* previous_next */}
              {(behavior === "previous_next" || behavior === "previous_ok") &&
                currentStep > 0 && (
                  <CustomButton
                    text="Previous"
                    className=" br-7 flex-fill bg-F2F4FB text-black2D"
                    textColor="text-black2D"
                    onClick={onPrev}
                  />
                )}

              {/* next */}
              {behavior === "next" && (
                <CustomButton
                  text={currentStep === steps.length - 1 ? "Finish" : "Next"}
                  className="bg-primary br-7 flex-fill"
                  onClick={() => {
                    if (onNext) {
                      onNext();
                    }
                  }}
                />
              )}

              {/* previous_next */}
              {behavior === "previous_next" && (
                <CustomButton
                  text={currentStep === steps.length - 1 ? "Finish" : "Next"}
                  className="bg-primary br-7 flex-fill"
                  onClick={onNext}
                />
              )}

              {/* ok */}
              {(behavior === "ok" || behavior === "previous_ok") && (
                <CustomButton
                  text="OK"
                  className="bg-primary br-7 flex-fill"
                  onClick={onCloseorDone}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulseGlow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }
      `}</style>
    </div>
  );

  return createPortal(overlayContent, document.body);
};

export default React.memo(OverlayGuide);
